import { Fragment, useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axios, { getBaseUrl } from '../../../services/api';
import FileUpload from '../../../components/common/FileUpload';
import { generateUUID } from '../../../services/utils';

export default function ECardModal({ isOpen, onClose, ecard, onSuccess }) {
  const { t } = useTranslation();
  const defaultMaleAvatar = '/uploads/general/agent-default-male.png';
  const defaultFemaleAvatar = '/uploads/general/agent-default-female.png';
  const [searchTerm, setSearchTerm] = useState('');
  const [assignedSips, setAssignedSips] = useState([]);
  const [webrtcAccounts, setWebrtcAccounts] = useState([]);
  const [defaultEcardId, setDefaultEcardId] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    ecardId: '',
    duty: '',
    email: '',
    gender: 'male',
    publicStatus: false,
    avatar: null,
    sipAccount: '',
    webrtcAccount: '',
  });

  const fetchSipAccounts = async () => {
    try {
      const response = await axios.get(`/ecards/sip-accounts`);
      setAssignedSips(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  // const fetchWebrtcAccounts = async () => {
  //   try {
  //     const response = await axios.get(
  //       `/settings/unassigned-sips?sipAccount=${ecard?.webrtcAccount}&type=webrtc`
  //     );
  //     setWebrtcAccounts(response.data || []);
  //   } catch (error) {
  //     console.error('Error fetching WebRTC accounts:', error);
  //     setWebrtcAccounts([]);
  //   }
  // };

  const fetchWebrtcAccounts = async () => {
    try {
      const response = await axios.get('/sip_account/accounts', {
        params: {
          category: 'webrtc',
          assignmentStatus: 'all',
          status: 'all',
          searchTerm: ''
        }
      });
      setWebrtcAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching WebRTC accounts:', error);
      setWebrtcAccounts([]);
    }
  };

  useEffect(() => {
    if (isOpen && ecard) {
      setFormData({
        name: ecard.name || '',
        ecardId: ecard?.ecardId || '',
        duty: ecard.duty || '',
        email: ecard.email || '',
        gender: ecard.gender || 'male',
        publicStatus: ecard.publicStatus || 'public', // public, private
        avatar: ecard.avatar || defaultMaleAvatar || '',
        sipAccount: ecard.sipAccount || '',
        webrtcAccount: ecard.webrtcAccount || '',
      });
      setDefaultEcardId(ecard?.ecardId || '');
    } else if (isOpen) {
      const defaultId = ecard?.ecardId || generateUUID();
      setFormData({
        name: '',
        ecardId: defaultId,
        duty: '',
        email: '',
        gender: 'male',
        publicStatus: 'public',
        avatar: defaultMaleAvatar || '',
        sipAccount: '',
        webrtcAccount: '',
      });
      setDefaultEcardId(defaultId);
    }

    fetchSipAccounts();
    fetchWebrtcAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ecard]);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'public' : 'private') : value,
    }));
  };

  const handleAvatarUpload = url => {
    setFormData(prev => ({
      ...prev,
      avatar: url?.file?.url || '',
    }));
  };

  const handleUploadError = error => {
    toast.error(error.message || 'Upload failed');
  };

  const handleSelectSip = sip => {
    setFormData(prev => ({
      ...prev,
      sipAccount: sip.sipAccount,
    }));
  };

  const handleSave = async () => {
    if (
      !formData.name.trim() ||
      !formData.duty ||
      !formData.sipAccount ||
      !formData.webrtcAccount
    ) {
      toast.error(t('Please input all required fields'));
      return;
    }

    try {
      if (ecard) {
        await axios.put(`/ecards/${ecard._id}`, formData);
        toast.success(t('Update Successfully'));
      } else {
        await axios.post('/ecards', formData);
        toast.success(t('Save Successfully'));
      }
      onSuccess();
      onClose();
    } catch (error) {
      // toast.error(t('Save Failed'));
      console.error('Error saving agent:', error);
    }
  };

  const filteredSips = assignedSips?.filter(
    sip =>
      sip.sipAccount.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sip?.ecardInfo && sip.ecardInfo?.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    if (!ecard) {
      setFormData(prev => ({
        ...prev,
        avatar: formData?.gender === 'male' ? defaultMaleAvatar : defaultFemaleAvatar,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.gender]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] app-text">
        <div className="bg-white w-[50vw] max-h-[85vh] rounded-lg shadow-xl flex flex-col">
          <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-semibold">{t('ECard')}</h2>
            <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
              <FaTimes />
            </button>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="space-y-3">
              <div className="flex gap-4 px-4 pt-3">
                <div className="flex-1 gap-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Access Domain Name')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={`${window.location.origin}`}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled
                    />
                    <input
                      type="text"
                      value={`/ecard?id=${defaultEcardId}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled
                    />
                  </div>
                </div>

                <div className="w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('WebRTC Account')}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.webrtcAccount}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, webrtcAccount: e.target.value }))
                    }
                    className="w-full h-[34px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">{t('Select Webrtc Account')}</option>
                    {webrtcAccounts.map(account => (
                      <option key={account._id} value={account.username}>
                        {account.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="px-4 py-3">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 flex gap-3 overflow-hidden px-4 pb-3">
              <div className="w-1/3 flex flex-col">
                <div className="p-1 bg-gray-50">
                  <h3 className="font-semibold text-gray-700">{t('Assigned Sip List')}</h3>
                </div>
                <div className="flex-1 border rounded-md overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-100 border-b">
                      <tr>
                        <th className="px-2 py-2 text-xs text-center nowrap font-semibold text-gray-600">
                          {t('No')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs nowrap font-semibold text-gray-600">
                          {t('Sip Account')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs nowrap font-semibold text-gray-600">
                          {t('Name')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSips.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-8 text-center text-gray-400">
                            {t('No result')}
                          </td>
                        </tr>
                      ) : (
                        filteredSips.map((sip, index) => (
                          <tr
                            key={sip.sipAccount}
                            className={`border-b cursor-pointer transition-colors ${
                              formData.sipAccount === sip.sipAccount
                                ? 'bg-green-100 hover:bg-green-200'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => handleSelectSip(sip)}
                          >
                            <td className="px-2 py-3 text-sm">
                              <span className="flex justify-center items-center">{index + 1}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div
                                className="max-w-[80px] truncate"
                                title={sip?.sipAccount || '--'}
                              >
                                {sip?.sipAccount || '--'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div
                                className="max-w-[60px] truncate"
                                title={sip.ecardInfo?.name || '--'}
                              >
                                {sip?.ecardInfo?.name || '--'}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex-1 flex flex-col overflow-y-auto">
                <div className="p-1 bg-gray-50">
                  <h3 className="font-semibold text-gray-700">{t('ECard Info')}</h3>
                </div>
                <div className="flex-1 flex flex-col border rounded-md px-6 py-4">
                  <div className="flex flex-1 gap-6">
                    <div className="flex-1 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('Name')}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Duty */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('Duty')}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="duty"
                          value={formData.duty}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('Sip Account')}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="sipAccount"
                          value={formData.sipAccount}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('email')}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('Gender')}
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="gender"
                              value="male"
                              checked={formData.gender === 'male'}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-sm">{t('Male')}</span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="gender"
                              value="female"
                              checked={formData.gender === 'female'}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-sm">{t('Female')}</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="publicStatus"
                            checked={formData.publicStatus === 'public'}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm font-medium text-gray-700">{t('isPublic')}</span>
                        </label>
                      </div>
                    </div>

                    <div className="w-48">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Avatar')}
                      </label>
                      <div className="w-48 h-48">
                        <FileUpload
                          value={formData.avatar}
                          onUploadSuccess={handleAvatarUpload}
                          onUploadError={handleUploadError}
                          accept="image/*"
                          allowedTypes={[
                            'image/jpeg',
                            'image/jpg',
                            'image/png',
                            'image/gif',
                            'image/webp',
                          ]}
                          preview={true}
                          showProgress={true}
                          uploadEndpoint="/upload/single"
                          baseUrl={getBaseUrl()}
                          className="w-full h-full"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <button
                      onClick={onClose}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      {t('Cancel')}
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                    >
                      {t('Save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
}
